import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    GlobalLimitedCostCreateInput,
    GlobalLimitedCostUpdateInput,
    GlobalOtherCostCreateInput,
    GlobalOtherCostUpdateInput,
} from 'gql/graphql';
import { ChapterEnum, OtherCostCalculationTypeEnum, type ProjectMethodologyEnum, type SectionEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    createGlobalLimitedCostGql,
    createGlobalOtherCostGql,
    updateGlobalLimitedCostGql,
    updateGlobalOtherCostGql,
} from '../catalogOtherAndLimitedQueries';
import { CatalogOtherAndLimitedTab } from '../types';

export type TSaveCatalogCostPayload = {
    id?: number;
    name: string;
    methodology: ProjectMethodologyEnum;
    formula: string;
    typeUniqueId: string;
    calculatingType: OtherCostCalculationTypeEnum;
    sectionId: string;
    isAdditionalTransport?: boolean;
};

export const useSaveCatalogCost = ({ tab, onSuccess }: { tab: CatalogOtherAndLimitedTab; onSuccess: () => void }) => {
    const dispatch = useDispatch();
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;

    const [resetCreateOther] = useResetStoreData({ requestId: 'createGlobalOtherCost' });
    const [resetUpdateOther] = useResetStoreData({ requestId: 'updateGlobalOtherCost' });
    const [resetCreateLimited] = useResetStoreData({ requestId: 'createGlobalLimitedCost' });
    const [resetUpdateLimited] = useResetStoreData({ requestId: 'updateGlobalLimitedCost' });

    const onCreateOtherSuccess = useCallback(
        ({ payload }: { payload?: { createGlobalOtherCost?: { result?: boolean } } }) => {
            if (payload?.createGlobalOtherCost?.result) {
                onSuccess();
                resetCreateOther(undefined);
            }
        },
        [onSuccess, resetCreateOther],
    );
    const onUpdateOtherSuccess = useCallback(
        ({ payload }: { payload?: { updateGlobalOtherCost?: { result?: boolean } } }) => {
            if (payload?.updateGlobalOtherCost?.result) {
                onSuccess();
                resetUpdateOther(undefined);
            }
        },
        [onSuccess, resetUpdateOther],
    );
    const onCreateLimitedSuccess = useCallback(
        ({ payload }: { payload?: { createGlobalLimitedCost?: { result?: boolean } } }) => {
            if (payload?.createGlobalLimitedCost?.result) {
                onSuccess();
                resetCreateLimited(undefined);
            }
        },
        [onSuccess, resetCreateLimited],
    );
    const onUpdateLimitedSuccess = useCallback(
        ({ payload }: { payload?: { updateGlobalLimitedCost?: { result?: boolean } } }) => {
            if (payload?.updateGlobalLimitedCost?.result) {
                onSuccess();
                resetUpdateLimited(undefined);
            }
        },
        [onSuccess, resetUpdateLimited],
    );

    const [, createOtherStatus, , createOtherRequest] = useRequest({
        requestId: 'createGlobalOtherCost',
        query: createGlobalOtherCostGql,
        withEffect: false,
        dependenciesUpdater: onCreateOtherSuccess,
    });
    const [, updateOtherStatus, , updateOtherRequest] = useRequest({
        requestId: 'updateGlobalOtherCost',
        query: updateGlobalOtherCostGql,
        withEffect: false,
        dependenciesUpdater: onUpdateOtherSuccess,
    });
    const [, createLimitedStatus, , createLimitedRequest] = useRequest({
        requestId: 'createGlobalLimitedCost',
        query: createGlobalLimitedCostGql,
        withEffect: false,
        dependenciesUpdater: onCreateLimitedSuccess,
    });
    const [, updateLimitedStatus, , updateLimitedRequest] = useRequest({
        requestId: 'updateGlobalLimitedCost',
        query: updateGlobalLimitedCostGql,
        withEffect: false,
        dependenciesUpdater: onUpdateLimitedSuccess,
    });

    const save = useCallback(
        (payload: TSaveCatalogCostPayload) => {
            if (isOthersTab) {
                if (payload.id) {
                    const input: GlobalOtherCostUpdateInput = {
                        id: payload.id,
                        name: payload.name,
                        catalogOtherCostTypeUniqueId: payload.typeUniqueId,
                        formula:
                            payload.calculatingType === OtherCostCalculationTypeEnum.Percent
                                ? payload.formula
                                : undefined,
                    };

                    dispatch(updateOtherRequest({ input }));

                    return;
                }

                const input: GlobalOtherCostCreateInput = {
                    name: payload.name,
                    methodology: payload.methodology,
                    section: payload.sectionId as SectionEnum,
                    calculatingType: payload.calculatingType,
                    catalogOtherCostTypeUniqueId: payload.typeUniqueId,
                    formula:
                        payload.calculatingType === OtherCostCalculationTypeEnum.Percent ? payload.formula : undefined,
                };

                dispatch(createOtherRequest({ input }));

                return;
            }

            const chapter = payload.sectionId as ChapterEnum;

            if (payload.id) {
                const input: GlobalLimitedCostUpdateInput = {
                    id: payload.id,
                    name: payload.name,
                    catalogLimitedCostTypeUniqueId: payload.typeUniqueId,
                    formula: payload.formula,
                };

                dispatch(updateLimitedRequest({ input }));

                return;
            }

            const input: GlobalLimitedCostCreateInput = {
                name: payload.name,
                methodology: payload.methodology,
                chapter: payload.isAdditionalTransport ? ChapterEnum.Chapter1_7 : chapter,
                catalogLimitedCostTypeUniqueId: payload.typeUniqueId,
                formula: payload.formula,
            };

            dispatch(createLimitedRequest({ input }));
        },
        [createLimitedRequest, createOtherRequest, dispatch, isOthersTab, updateLimitedRequest, updateOtherRequest],
    );

    const isSaving = [createOtherStatus, updateOtherStatus, createLimitedStatus, updateLimitedStatus].includes(
        RequestStatus.FETCHING,
    );

    return useMemo(
        () => ({
            save,
            isSaving,
        }),
        [isSaving, save],
    );
};
